/**
 * RL-1142 the doctor's video consults (API RL-1003 video sessions, RL-1002
 * triage/consent/payment gate and patient requests, RL-1004 outcome). Shapes
 * follow ommri-care-api `video.dto.ts` / `video-consults.dto.ts`.
 */

export type VideoStatus =
  | 'scheduled'
  | 'waiting'
  | 'admitted'
  | 'in_call'
  | 'ended'
  | 'cancelled'
  | 'expired';

export const LIVE_STATUSES: readonly VideoStatus[] = [
  'scheduled',
  'waiting',
  'admitted',
  'in_call',
];

export type VideoBlocker =
  | 'triage_required'
  | 'triage_not_suitable'
  | 'consent_required'
  | 'payment_required';

export type VideoOutcomeResult =
  'complete' | 'come_in' | 'incomplete' | 'dropped';
export const OUTCOME_RESULTS: readonly VideoOutcomeResult[] = [
  'complete',
  'come_in',
  'incomplete',
  'dropped',
];

export type VideoEndReason =
  | 'doctor_ended'
  | 'teleconsult_closed'
  | 'converted'
  | 'cancelled'
  | 'expired'
  | 'patient_dropped';

export type VideoPaymentState =
  | 'not_required'
  | 'recorded'
  | 'due'
  | 'pay_later'
  | 'paid'
  | 'covered'
  | 'waived';

export type TeleconsultType = 'follow_up' | 'first_visit';

export interface VideoTriage {
  isEmergency: boolean;
  needsExamination: boolean;
  canSpeakOnCall: boolean;
  symptoms: string | null;
  duration: 'today' | 'days_1_3' | 'days_4_7' | 'over_week' | null;
  result: 'ok' | 'emergency' | 'in_person';
}

export interface VideoSessionTriage extends VideoTriage {
  guidance: { en: string; ms: string } | null;
  submittedAt: string | null;
}

export interface VideoPayment {
  state: VideoPaymentState;
  feeSen: number | null;
  youPaySen: number | null;
  dueSen: number;
  payer: { id: string; name: string } | null;
  billId: string | null;
  reference: string | null;
  payAt: 'counter';
  inAppPaymentAvailable: boolean;
  payLaterAt: string | null;
  refundDue: boolean;
}

export interface StaffVideoOutcome {
  result: VideoOutcomeResult;
  patientNote: string | null;
  chargeable: boolean;
  followUpBy: string | null;
  appointmentId: string | null;
  source: 'doctor' | 'patient' | 'system';
  recordedAt: string;
  recordedById: string | null;
}

export interface StaffVideoSession {
  id: string;
  teleconsultId: string;
  teleconsultStatus: 'booked' | 'in_progress' | 'closed' | 'converted';
  status: VideoStatus;
  type: TeleconsultType;
  startsAt: string;
  window: {
    state: 'not_open' | 'open' | 'closed';
    opensAt: string;
    joinClosesAt: string;
    endsBy: string;
  };
  patient: { id: string; fullName: string };
  doctor: { id: string; fullName: string; mmcNumber: string | null };
  branch: { id: string; name: string };
  mine: boolean;
  waitingRoom: {
    since: string | null;
    cameraReady: boolean | null;
    microphoneReady: boolean | null;
  };
  admittedAt: string | null;
  identityCheckedAt: string | null;
  patientJoinedAt: string | null;
  doctorJoinedAt: string | null;
  patientLeftAt: string | null;
  rejoinBy: string | null;
  endedAt: string | null;
  endReason: VideoEndReason | null;
  outcome: StaffVideoOutcome | null;
  outcomePending: boolean;
  triage: VideoSessionTriage | null;
  consent: {
    accepted: boolean;
    acceptedAt: string | null;
    version: string | null;
    currentVersion: string;
  };
  payment: VideoPayment;
  blockers: VideoBlocker[];
  version: number;
}

export interface StaffVideoSessionList {
  date: string;
  /** false = video is switched off on this server (tokens answer 503). */
  videoAvailable: boolean;
  rows: StaffVideoSession[];
}

export interface VideoCallToken {
  /** 'fake' only on local/test servers: the token joins nothing. */
  provider: 'getstream' | 'fake';
  apiKey: string;
  callType: string;
  callId: string;
  participant: 'patient' | 'doctor';
  userId: string;
  userName: string;
  token: string;
  expiresAt: string;
}

export interface VideoOutcomeInput {
  result: VideoOutcomeResult;
  patientNote?: string | null;
  chargeable?: boolean | null;
  followUpBy?: string | null;
  appointmentId?: string | null;
}

export type RequestStatus =
  'requested' | 'accepted' | 'declined' | 'cancelled' | 'expired';

export interface StaffVideoConsultRequest {
  id: string;
  status: RequestStatus;
  branch: { id: string; name: string };
  triage: VideoTriage | null;
  consent: { acceptedAt: string; version: string };
  payer: { id: string; name: string } | null;
  price: {
    youPaySen: number;
    usualSen: number | null;
    memberPriceApplies: boolean;
  };
  payment: { payAt: 'counter'; inAppPaymentAvailable: boolean };
  videoSession: { id: string; startsAt: string } | null;
  declineReason: string | null;
  createdAt: string;
  expiresAt: string;
  decidedAt: string | null;
  cancelledAt: string | null;
  version: number;
  patient: { id: string; fullName: string };
  teleconsultId: string | null;
  decidedById: string | null;
}

export interface StaffVideoConsultRequestList {
  status: RequestStatus;
  rows: StaffVideoConsultRequest[];
}

/**
 * RL-1900: GET/PUT /video-consults/fee (API `VideoFeeDto`). One fee for every
 * branch; null = not set (patients cannot ask for a video consult).
 */
export interface VideoFee {
  priceSen: number | null;
  memberPriceSen: number | null;
  updatedAt: string | null;
  updatedById: string | null;
}

/** The member price is set under Discount schemes (API RL-1901), not here. */
export interface VideoFeeInput {
  priceSen: number | null;
}

/** RL-2158 (staging): one recording of a video consult. No bucket key or URL is ever sent. */
export type RecordingStatus =
  'requested' | 'recording' | 'processing' | 'ingesting' | 'ready' | 'failed';

export interface VideoRecording {
  id: string;
  status: RecordingStatus;
  mode: 'audio_only' | 'composite';
  startedAt: string | null;
  stoppedAt: string | null;
  stopReason: string | null;
  readyAt: string | null;
  durationSeconds: number | null;
  sizeBytes: number | null;
  contentType: string | null;
  failureReason: string | null;
  canPlay: boolean;
  createdAt: string;
}

export interface VideoRecordingList {
  enabled: boolean;
  recordings: VideoRecording[];
}

/** POST /video-recordings/{id}/access: a signed link of about two minutes. */
export interface VideoRecordingAccess {
  url: string;
  expiresAt: string;
  contentType: string | null;
}
